import React, { useEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useSearchParams } from "react-router";
import { useSession } from "@clerk/clerk-react";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { v4 as uuidv4 } from "uuid";
import { useEditPrd } from "./hooks/useEditPrd";
import { useIssueData } from "./hooks/useIssueData";
import PRDHeader from "./components/PRDHeader";
import PRDEditor from "./components/PRDEditor";
import PRDProperties from "./components/PRDProperties";
import mermaid from "mermaid";
import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import { CanvasPreview } from "@/components/Canvas/CanvasPreview";

mermaid.initialize({
  startOnLoad: false,
  theme: "dark",
  securityLevel: "strict",
  suppressErrorRendering: true,
});

type PRDModalProps = {
  canvasId: string;
  projectId: string;
  isOpen: boolean;
  closeModal: () => void;
};

const PRDModal: React.FC<PRDModalProps> = ({
  canvasId,
  projectId,
  isOpen,
  closeModal,
}) => {
  const currentProject = useSelector((state: any) =>
    projectId ? state.supabaseStore.projects?.[projectId] : null,
  );

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const usersForProject = useCanvasStore((state) => state.usersForProject);

  const currentIssue = useIssuesStore(
    (state) => state.canvasIdToIssue[canvasId ?? ""],
  );

  const featurePrd = useMemo(() => {
    return currentIssue?.featurePrd;
  }, [currentIssue?.featurePrd]);

  const featurePrdRef = useRef(featurePrd);

  useEffect(() => {
    featurePrdRef.current = featurePrd;
  }, [featurePrd]);

  const { issue, issueData } = useIssueData({
    canvas: currentIssue?.canvas,
    project: currentProject,
  });

  const onEditPrd = useEditPrd(canvasId);

  useEffect(() => {
    if (!supabaseClient || !canvasId) {
      return;
    }

    const createPrd = async () => {
      const newPrd: any = {
        id: uuidv4().toString(),
        canvas_id: canvasId,
      };

      // Use the createFeaturePRD function from the store instead of onDbSnapshot
      const createFeaturePRD = useIssuesStore.getState().createFeaturePRD;
      if (createFeaturePRD) {
        createFeaturePRD(supabaseClient, newPrd);
      }
    };

    if (!featurePrd && currentIssue) {
      createPrd();
    }
  }, [featurePrd, currentIssue, supabaseClient, canvasId]);

  const urlHasCanvasId =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("canvas_id") === canvasId;

  if (isOpen && !currentIssue?.canvas && !urlHasCanvasId) {
    closeModal();
    return null;
  }

  const shouldShowCanvasPreview =
    (currentIssue?.canvas?.storyboard_count ?? 0) > 0;

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent
        className={cn(
          "max-w-[960px] w-full p-0 bg-background-primary border border-border-primary overflow-hidden [&>button>svg]:w-4! [&>button>svg]:h-4! [&>button>svg]:bg-transparent [&>button>svg]:p-0 [&>button>svg]:m-1",
          "top-98 -translate-y-88",
        )}
        hideClose={false}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex flex-col h-full">
          <PRDHeader project={currentProject} issue={issue} />
          {shouldShowCanvasPreview && (
            <div className="shrink-0 border-b">
              <CanvasPreview canvasId={canvasId} />
            </div>
          )}

          <div className="flex h-auto max-h-[calc(100vh-10rem)]">
            <div className="flex flex-col flex-1 h-full min-w-0">
              <div
                className="flex-1 min-h-0 max-h-[800px] overflow-y-auto scrollbar-thin scrollbar-thumb-border-primary scrollbar-track-transparent"
                style={{
                  WebkitOverflowScrolling: "touch", // for smooth scrolling on iOS
                }}
              >
                <PRDEditor
                  featurePrd={featurePrd as any}
                  onEditPrd={onEditPrd}
                />
              </div>
            </div>

            <PRDProperties
              project={currentProject}
              issue={issue}
              issueData={issueData}
              usersForProject={usersForProject}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PRDModal;
